import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PUBLIC_WHATSAPP_PHONE, whatsappHref } from "~/config/whatsappSupport";
import { cn } from "~/lib/utils";

/**
 * Acciones flotantes de la superficie pública: volver arriba + consulta por WhatsApp.
 *
 * Se apilan en la esquina inferior derecha (WhatsApp abajo, "subir" encima) para no
 * pisarse. El botón de subir sólo aparece pasada una pantalla de scroll.
 *
 * WhatsApp abre un panel con consultas ya escritas: el usuario elige una y sale a wa.me
 * con el texto puesto. Bajar la fricción de "¿y qué le escribo?" es justamente lo que
 * hace que la gente escriba.
 */

type Ask = { label: string; text: string };

export function FloatingActions() {
  const { t } = useTranslation();
  const [showTop, setShowTop] = useState(false);
  // Alto de una barra fija inferior (el CTA sticky del detalle de curso, p.ej.): las
  // acciones se levantan por encima para no taparla.
  const [barOffset, setBarOffset] = useState(0);
  const [waOpen, setWaOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const waBtnRef = useRef<HTMLButtonElement>(null);

  const asks = t("landing.wa.asks", { returnObjects: true }) as Ask[];

  // Los DOS botones aparecen recién cuando ya se bajó casi una pantalla. El de WhatsApp
  // también (antes iba siempre): en mobile quedaba plantado justo encima del CTA del hero
  // "empezá tu curso gratis acá →" y la flecha terminaba apuntándole al WhatsApp. El canal
  // de contacto sigue a mano: en cuanto scrolleás está, y en el footer vive su ícono.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setShowTop(window.scrollY > window.innerHeight * 0.8);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Medir la barra fija inferior si la hay. Se re-mide ante cambios de DOM (navegación
  // SPA: la barra aparece/desaparece según la página) y ante resize/orientación.
  //
  // DOS CUIDADOS, los dos por rendimiento y los dos aprendidos acá:
  //
  // 1. El observer ignora los subárboles que ANIMAN. La home tiene videos que rotan solos
  //    y SVG animado; observando `document.body` entero, cada cuadro de esas animaciones
  //    disparaba una medición. Ahora se saltean las mutaciones que ocurren dentro de un
  //    `[data-fab-ignore]` o del propio bloque flotante.
  // 2. Sólo se llama a setState cuando el número CAMBIA. `setBarOffset(0)` con el valor
  //    ya en 0 igual hace que React re-renderice el componente antes de descartar el
  //    cambio, y esto corría muchas veces por segundo.
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const bar = document.querySelector<HTMLElement>("[data-bottom-bar]");
        // getBoundingClientRect().height da 0 si está oculta por CSS (lg:hidden en desktop).
        const next = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
        setBarOffset((prev) => (prev === next ? prev : next));
      });
    };
    measure();
    const mo = new MutationObserver((muts) => {
      const relevante = muts.some(
        (m) => !(m.target as Element)?.closest?.("[data-fab-ignore], [data-fab-root]"),
      );
      if (relevante) measure();
    });
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    return () => {
      mo.disconnect();
      window.removeEventListener("resize", measure);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Si el usuario vuelve arriba con el panel abierto, el botón se esconde: el panel
  // no puede quedar huérfano flotando sin su botón.
  useEffect(() => {
    if (!showTop) setWaOpen(false);
  }, [showTop]);

  // Cerrar el panel con Escape o al tocar fuera.
  useEffect(() => {
    if (!waOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setWaOpen(false);
        waBtnRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || waBtnRef.current?.contains(target)) return;
      setWaOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [waOpen]);

  return (
    <div
      data-fab-root
      className="pointer-events-none fixed right-4 z-50 flex flex-col items-end gap-3 sm:right-6"
      style={{
        bottom: `calc(${barOffset ? barOffset + 16 : 20}px + env(safe-area-inset-bottom, 0px))`,
      }}
    >
      {/* Volver arriba */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        data-cuelume-press
        aria-label={t("landing.wa.toTop")}
        className={cn(
          // Mismo diámetro (56px) y misma sombra que el de WhatsApp: son un solo sistema.
          // El borde se queda sólo acá porque este botón es claro sobre fondo claro y sin
          // él pierde el contorno; el de WhatsApp lo resuelve con su propio color.
          // Misma capa propia que el de WhatsApp: este también es un círculo sobre el
          // hero animado, y aunque hoy se ve menos (aparece recién con scroll, donde
          // detrás ya hay secciones quietas), el mecanismo es idéntico.
          "pointer-events-auto flex h-14 w-14 transform-gpu items-center justify-center rounded-full border-[1.5px] border-landing-border bg-landing-paper text-landing-text shadow-landing-lg transition-all duration-200 hover:-translate-y-0.5 hover:border-landing-red hover:text-landing-red",
          showTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
        )}
      >
        {/* 28px como el logo de WhatsApp. El path se estira dentro del viewBox a propósito:
            el logo llena casi todo su 24x24, y la flecha original ocupaba 12x14, así que a
            igual tamaño de svg el glifo pesaba la mitad. Ahora ocupa 16x18 de 24. */}
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7">
          <path
            d="M12 21V3M12 3l-8 8M12 3l8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Panel de consultas.

          Va ABSOLUTO, fuera del flujo del flex. Estando dentro del flujo se insertaba
          entre el botón de "volver arriba" y el de WhatsApp, y al abrirse empujaba el
          de volver-arriba hacia arriba: el usuario abre un panel y otro botón se le
          escapa de abajo del dedo. Ahora el panel se dibuja ENCIMA (z-10) y nada se
          mueve. `bottom-[68px]` = 56px del botón de WhatsApp + los 12px del gap. */}
      {waOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t("landing.wa.title")}
          // `transform-gpu`: el panel vive en su propia capa de composición, así no se
          // recompone junto con el hero, que detrás de él anima todo el tiempo (arcos y
          // partículas). Es una precaución contra el parpadeo reportado en mobile con la
          // página arriba de todo — NO está confirmado que sea la causa: midiendo el
          // interior del panel cuadro a cuadro sale idéntico, su DOM no muta y no tiene
          // animaciones adentro, así que el parpadeo no se pudo reproducir en headless.
          // Si vuelve a aparecer, los dos sospechosos siguientes son la barra de URL del
          // navegador móvil (cambia el viewport y mueve los elementos `fixed`) y los dos
          // elementos con `backdrop-filter` que hay en la página, que en Android son
          // causa conocida de flicker cuando algo fijo se dibuja encima.
          //
          // `origin-bottom-right` + eorise: además crece desde el botón que lo abrió, en
          // vez de materializarse de golpe.
          className="pointer-events-auto absolute bottom-[68px] right-0 z-10 w-[min(310px,calc(100vw-32px))] origin-bottom-right transform-gpu animate-eorise overflow-hidden rounded-[16px] border-[1.5px] border-landing-border bg-landing-paper shadow-landing-xl motion-reduce:animate-none"
        >
          <div className="flex items-center gap-2 bg-[#25D366] px-4 py-3">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-landing-on-dark">
              <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.2 8.2 0 0 1 8.24 8.24c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
            </svg>
            <span className="text-[14px] font-bold text-landing-on-dark">
              {t("landing.wa.title")}
            </span>
          </div>
          <p className="px-4 pb-1 pt-3 text-[12.5px] leading-snug text-landing-muted">
            {t("landing.wa.lead")}
          </p>
          <ul className="flex flex-col gap-1.5 p-3 pt-2">
            {asks.map((ask) => (
              <li key={ask.label}>
                <a
                  href={whatsappHref(ask.text, PUBLIC_WHATSAPP_PHONE)}
                  target="_blank"
                  rel="noreferrer"
                  data-cuelume-press
                  onClick={() => setWaOpen(false)}
                  className="block rounded-[10px] border border-landing-border bg-landing-white px-3 py-2.5 text-[13.5px] font-semibold leading-snug text-landing-text transition-colors hover:border-[#25D366] hover:bg-landing-surface"
                >
                  {ask.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Botón de WhatsApp */}
      <button
        ref={waBtnRef}
        type="button"
        onClick={() => setWaOpen((v) => !v)}
        data-cuelume-press
        aria-expanded={waOpen}
        aria-label={t("landing.wa.open")}
        // `transform-gpu`: capa propia, igual que el panel. CONFIRMADO en dispositivo:
        // con la capa el panel dejó de parpadear y este botón siguió haciéndolo, porque
        // se había quedado sin ella. Es el mismo mecanismo — un borde redondeado que se
        // recompone contra el hero animado que tiene detrás — y por eso sólo se ve
        // arriba de todo y en mobile, que es donde el hero ocupa la pantalla.
        className={cn(
          "pointer-events-auto relative flex h-14 w-14 transform-gpu items-center justify-center overflow-hidden rounded-full bg-[#25D366] shadow-landing-lg transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105",
          showTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
        )}
      >
        {/* Los DOS íconos viven siempre montados y superpuestos: el que entra gira hasta
            0° mientras aparece, el que sale gira y se encoge. Montar/desmontar según el
            estado daba un corte seco — el ícono desaparecía y el otro aparecía sin nada
            en el medio. Al girar en sentidos opuestos (+90° la cruz, −90° el logo), el
            gesto se lee como una sola pieza que se transforma, ida y vuelta.
            `motion-reduce` deja el cambio instantáneo para quien pidió no ver animación. */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none",
            waOpen ? "rotate-0 scale-100 opacity-100" : "rotate-90 scale-50 opacity-0",
          )}
        >
          <svg viewBox="0 0 24 24" className="h-7 w-7">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="#fff"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none",
            waOpen ? "-rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100",
          )}
        >
          <svg viewBox="0 0 24 24" className="h-7 w-7 fill-landing-on-dark">
            <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.2 8.2 0 0 1 8.24 8.24c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.16 1.71 2.61 4.15 3.66.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
          </svg>
        </span>
      </button>
    </div>
  );
}
